import type { ElectronStoreBridge } from "@observe-tech/utils";
import type { HostCapabilities } from "./index";

export type { ElectronStoreBridge } from "@observe-tech/utils";

const BRIDGE_MEMBERS: Record<keyof ElectronStoreBridge, true> = {
  getScreenId: true,
  getDisplayScreens: true,
  openScreens: true,
  focusScreen: true,
  getOpenScreenIds: true,
  getScreenArrangement: true,
  writeIpJsonFile: true,
  removeIpJsonFile: true,
  readLegacyConfig: true,
  readIpJsonFile: true,
  onIpJsonEdited: true,
  onReadIpJson: true,
  pushAutoUpdateInputs: true,
  applyDatarouterConfig: true,
  syncIntegrationToAllWindows: true,
};

const assertCompleteBridge = (bridge: ElectronStoreBridge): void => {
  const missing = (
    Object.keys(BRIDGE_MEMBERS) as (keyof ElectronStoreBridge)[]
  ).filter((name) => typeof bridge[name] !== "function");
  if (missing.length > 0)
    throw new Error(`electronAPI is missing: ${missing.join(", ")}`);
};

export const createElectronCapabilities = (
  bridge: ElectronStoreBridge,
  options: { screenId?: number } = {},
): HostCapabilities => {
  assertCompleteBridge(bridge);
  const screenId = options.screenId ?? bridge.getScreenId();
  return {
    screens: {
      current: () => screenId,
      displays: () => bridge.getDisplayScreens(),
      getScreenArrangement: (ids) => bridge.getScreenArrangement(ids),
    },
    windows: {
      open: (ids) => bridge.openScreens(ids),
      focus: (id) => bridge.focusScreen(id),
      connectedScreenIds: () => bridge.getOpenScreenIds(),
    },
    files: {
      writeIpJson: (content) => bridge.writeIpJsonFile(content),
      removeIpJson: () => bridge.removeIpJsonFile(),
      readLegacyConfig: () => bridge.readLegacyConfig(),
      readLegacyIpJson: () => bridge.readIpJsonFile(),
      onIpJsonEdited: (callback) =>
        bridge.onIpJsonEdited((_event, content) => callback(content)),
      onReadIpJson: (callback) => bridge.onReadIpJson(callback),
    },
    updater: {
      pushAutoUpdateInputs: (inputs) => bridge.pushAutoUpdateInputs(inputs),
      applyDatarouterConfig: (config) => bridge.applyDatarouterConfig(config),
    },
    syncIntegration: async (changeType) => {
      const result = await bridge.syncIntegrationToAllWindows(changeType);
      if (!result.success) throw new Error("integration sync failed");
    },
  };
};
